import {
  type DatabaseProjectionQuery,
  type DatabaseProjectionSubscriptionResult,
  projectionCacheKey,
  toFrontendProjectionResult,
} from '@blocksuite/data-view';
import { effect, type Signal, signal } from '@preact/signals-core';
import type { DatabaseProjectionPage } from '@streamlain/database-identity';
import { cloneDeep } from 'lodash-es';

export type DatabaseProjectionProvider = {
  project(
    input: {
      rootDocId: string;
      databaseId: string;
      query: DatabaseProjectionQuery;
      generation: number;
      nowMs: number;
    },
    signal: AbortSignal
  ): Promise<
    DatabaseProjectionPage & {
      generation: number;
      revision: string;
      /** Local documents the answer read before they were reconciled with the server. */
      pendingDocIds: readonly string[];
    }
  >;
  /**
   * The distinct option names of one text column across every table part,
   * read by the worker without loading the rows (`databaseTextOptionNames`).
   */
  optionNames(
    input: {
      rootDocId: string;
      databaseId: string;
      columnId: string;
      toType: string;
    },
    signal: AbortSignal
  ): Promise<{
    names: readonly string[];
    everyShardRead: boolean;
    overBudget: boolean;
  }>;
  subscribe(callback: (docId?: string) => void): () => void;
  /**
   * Resolves when the listed local documents are reconciled with the server.
   * An answer that read them before that is incomplete although no document
   * changes afterwards, so the query is computed once more.
   */
  reconciled?(docIds: readonly string[], signal: AbortSignal): Promise<void>;
};
export const databaseProjectionProviders = new WeakMap<
  object,
  Set<DatabaseProjectionProvider>
>();

/** A consumer keeps its identity when its window or filters change. */
export function databaseProjectionConsumerKey(
  owner: string,
  query: DatabaseProjectionQuery
): string {
  const groupBy = query.groupBy;
  const dimensions = (
    Array.isArray(groupBy) ? groupBy : groupBy ? [groupBy] : []
  ).map(group => ({ columnId: group.columnId, name: group.name }));
  return projectionCacheKey(owner, {
    viewId: query.viewId,
    scope: query.scope,
    dimensions,
    requestedPropertyIds: query.requestedPropertyIds,
    aggregates: query.aggregates,
  });
}

export function emptyDatabaseProjection(
  query: DatabaseProjectionQuery,
  syncing: boolean,
  message?: string
): DatabaseProjectionSubscriptionResult {
  return {
    query,
    groups: [],
    aggregates: [],
    rowIds: [],
    complete: false,
    freshness: 'stale',
    syncState: syncing
      ? 'syncing'
      : message !== undefined
        ? 'unsupported'
        : 'partial',
    source: 'local-window',
    totalCount: 0,
    totalKnown: false,
    window: {
      offset: query.window?.offset ?? 0,
      limit: query.window?.limit ?? 100,
    },
    diagnostics:
      message !== undefined ? [{ code: 'partial-source', message }] : [],
  };
}

/** An unavailable snapshot has no selected rows or revision to hydrate yet. */
function isPartialEmptyProjection(
  result: Pick<
    DatabaseProjectionSubscriptionResult,
    'complete' | 'syncState' | 'rowIds' | 'groups'
  >
): boolean {
  return (
    result.syncState === 'partial' &&
    !result.complete &&
    !result.rowIds.length &&
    !result.groups.length
  );
}

/** Owns only this source's query subscriptions and selected row windows. */
export class DatabaseProjectionController {
  private readonly entries = new Map<
    string,
    {
      key: string;
      viewId?: string;
      aggregate?: boolean;
      value: Signal<DatabaseProjectionSubscriptionResult>;
      dispose: () => void;
    }
  >();
  private generation = 0;
  private subscriberId = 0;
  private readonly viewRevisions = new Map<string, string>();
  constructor(
    private readonly options: {
      provider: DatabaseProjectionProvider;
      rootDocId: string;
      databaseId: string;
      relevant: (docId: string) => boolean;
      hydrate: (owner: string, rowIds: readonly string[]) => boolean;
      release: (owner: string) => void;
    }
  ) {}

  get(
    query: DatabaseProjectionQuery,
    owner = 'cached-view'
  ): DatabaseProjectionSubscriptionResult {
    const slot = databaseProjectionConsumerKey(owner, query);
    const key = projectionCacheKey('query', query);
    const old = this.entries.get(slot);
    if (old?.key === key) {
      this.entries.delete(slot);
      this.entries.set(slot, old);
      return this.hydrated(slot, old.value.value);
    }
    old?.dispose();
    const value = signal(emptyDatabaseProjection(query, true));
    let disposed = false;
    let queued = false;
    let running = false;
    let abort: AbortController | undefined;
    let awaitingReconcile = false;
    let recomputedAfterReconcile = false;
    const reconcile = new AbortController();
    const recomputeOnceReconciled = (docIds: readonly string[]) => {
      const reconciled = this.options.provider.reconciled;
      if (
        !reconciled ||
        !docIds.length ||
        awaitingReconcile ||
        recomputedAfterReconcile
      )
        return;
      awaitingReconcile = true;
      reconciled(docIds, reconcile.signal).then(
        () => {
          awaitingReconcile = false;
          if (disposed) return;
          recomputedAfterReconcile = true;
          queued = true;
          if (!running) queueMicrotask(() => void run());
        },
        () => {
          awaitingReconcile = false;
        }
      );
    };
    const run = async () => {
      if (disposed || running) return;
      running = true;
      queued = false;
      abort = new AbortController();
      const generation = ++this.generation;
      // The last answer stays readable while the next one is computed: a
      // consumer never sees its rows vanish because one of them was edited.
      try {
        const page = await this.options.provider.project(
          {
            rootDocId: this.options.rootDocId,
            databaseId: this.options.databaseId,
            // View filters, grouping and sorting come from reactive Y proxies.
            // Capture a detached value tree before the asynchronous worker RPC.
            query: cloneDeep(query),
            generation,
            nowMs: Date.now(),
          },
          abort.signal
        );
        if (
          disposed ||
          abort.signal.aborted ||
          page.generation !== generation ||
          (page.syncState !== 'unsupported' &&
            !isPartialEmptyProjection(page) &&
            !page.revision)
        ) {
          if (disposed || abort.signal.aborted) return;
          throw new Error('The database projection could not be verified.');
        }
        if (page.syncState === 'stale') {
          // A document changed while it was read. This empty page answers
          // nothing; the last answer stays until the read is repeated.
          queued = true;
          return;
        }
        const ids = [
          ...new Set([
            ...page.rowIds,
            ...page.groups.flatMap(group => group.rowIds),
          ]),
        ];
        if (page.syncState !== 'unsupported' && !isPartialEmptyProjection(page))
          this.options.hydrate(slot, ids);
        value.value = {
          ...toFrontendProjectionResult(page, query),
          groups: page.groups,
        };
        if (!page.complete) recomputeOnceReconciled(page.pendingDocIds);
      } catch (error) {
        if (!disposed && !abort.signal.aborted)
          value.value = emptyDatabaseProjection(
            query,
            false,
            error instanceof Error
              ? error.message
              : 'The database could not be loaded.'
          );
      } finally {
        running = false;
        if (queued && !disposed) queueMicrotask(() => void run());
      }
    };
    const unsubscribe = this.options.provider.subscribe(docId => {
      if (docId && !this.options.relevant(docId)) return;
      queued = true;
      recomputedAfterReconcile = false;
      if (!docId) {
        abort?.abort();
        value.value = emptyDatabaseProjection(query, true);
      }
      // The worker fences revisions during a read; coalesce its own sync updates.
      if (!running) queueMicrotask(() => void run());
    });
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      abort?.abort();
      reconcile.abort();
      unsubscribe();
      this.options.release(slot);
    };
    this.entries.set(slot, {
      key,
      value,
      dispose,
      viewId: query.viewId,
      aggregate: !!query.aggregates?.length,
    });
    while (this.entries.size > 32) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) break;
      this.entries.get(oldest)?.dispose();
      this.entries.delete(oldest);
    }
    queueMicrotask(() => void run());
    return this.hydrated(slot, value.value);
  }
  subscribe(
    query: DatabaseProjectionQuery,
    subscriber: (result: DatabaseProjectionSubscriptionResult) => void,
    observeHydration: () => void
  ): () => void {
    const owner = `subscriber:${++this.subscriberId}`;
    const stop = effect(() => {
      observeHydration();
      subscriber(this.get(query, owner));
    });
    return () => {
      stop();
      for (const [key, entry] of this.entries)
        if (key.startsWith(`${owner}:`)) {
          entry.dispose();
          this.entries.delete(key);
        }
    };
  }
  setViewRevision(viewId: string, revision: string): void {
    const old = this.viewRevisions.get(viewId);
    if (old !== undefined && old !== revision) this.releaseView(viewId);
    this.viewRevisions.set(viewId, revision);
  }
  releaseViewRows(viewId: string, query: DatabaseProjectionQuery): void {
    // Switching one cached consumer to a local window must not cancel a
    // temporal/group consumer or an explicit subscription on the same view.
    const key = databaseProjectionConsumerKey('cached-view', query);
    const entry = this.entries.get(key);
    if (entry?.viewId === viewId && !entry.aggregate) {
      entry.dispose();
      this.entries.delete(key);
    }
  }
  releaseView(viewId: string): void {
    for (const [key, entry] of this.entries)
      if (entry.viewId === viewId) {
        entry.dispose();
        this.entries.delete(key);
      }
  }
  private hydrated(
    owner: string,
    result: DatabaseProjectionSubscriptionResult
  ): DatabaseProjectionSubscriptionResult {
    if (
      result.syncState === 'syncing' ||
      result.syncState === 'unsupported' ||
      isPartialEmptyProjection(result)
    )
      return result;
    const ids = [
      ...new Set([
        ...result.rowIds,
        ...result.groups.flatMap(group => group.rowIds),
      ]),
    ];
    if (this.options.hydrate(owner, ids)) return result;
    return {
      ...result,
      complete: false,
      totalKnown: false,
      syncState: 'partial',
      freshness: 'stale',
      groups: result.groups.map(group => ({
        ...group,
        complete: false,
        totalKnown: false,
      })),
      diagnostics: [
        ...result.diagnostics,
        {
          code: 'partial-source',
          message: 'The selected rows are still loading.',
        },
      ],
    };
  }
  optionNames(columnId: string, toType: string, signal: AbortSignal) {
    return this.options.provider.optionNames(
      {
        rootDocId: this.options.rootDocId,
        databaseId: this.options.databaseId,
        columnId,
        toType,
      },
      signal
    );
  }
  dispose(): void {
    for (const entry of this.entries.values()) entry.dispose();
    this.entries.clear();
  }
}
